/**
 * File Handler
 * Handles file/folder input, drop zone, and lyrics extraction
 */

import { audioState, playlistState, uiState, exportState } from './state.js';
import { elements } from './dom-cache.js';
import { entitlementService } from '../entitlements/entitlement-service.js';
import { featureGate } from '../entitlements/feature-gate.js';
import { updatePlaylistUI } from './playlist.js';
import { loadTrack, startPlayback, formatTime } from './playback.js';
import { exitLiveMode } from './live-mode.js';
import { initializeCustomOrder } from './playlist-overlay.js';
import { trackFilesLoaded } from './analytics/event-tracker.js';

// Track duration initialization state
let durationInitController = null;

// Guard against concurrent file processing
let isProcessingFiles = false;

/**
 * Update duration display for a single track without full DOM refresh.
 * This preserves scroll state and avoids triggering playlistUpdated events.
 * @param {number} index - Track index in the playlist
 * @param {number} duration - Duration in seconds
 */
function updateTrackDuration(index, duration) {
  const formattedDuration = formatTime(duration);

  // Update sidebar playlist
  const sidebarItem = document.querySelector(
    `.playlist-item[data-index="${index}"] .playlist-item-duration`
  );
  if (sidebarItem) {
    sidebarItem.textContent = formattedDuration;
  }

  // Update overlay playlist (if visible)
  const overlayItem = document.querySelector(
    `.playlist-overlay-item[data-index="${index}"] .song-duration`
  );
  if (overlayItem) {
    overlayItem.textContent = formattedDuration;
  }
}

/**
 * Extract lyrics from a Suno metadata txt file
 */
export async function extractLyrics(txtFile) {
  const text = await txtFile.text();

  // Find "--- Lyrics ---" section
  const lyricsMatch = text.match(/--- Lyrics ---\n([\s\S]*?)(?=\n---|\n\n--- |$)/);
  if (!lyricsMatch) return null;

  let lyrics = lyricsMatch[1];

  // Remove entire lines that are only bracket annotations [Verse 1], [Hook | description], etc.
  lyrics = lyrics.replace(/^\s*\[.*?\]\s*$/gm, '');

  // Also remove any remaining inline bracket annotations
  lyrics = lyrics.replace(/\[.*?\]/g, '');

  // Remove plain text section headers (Verse 1, Pre-Chorus, Chorus, Bridge, etc.)
  lyrics = lyrics.replace(/^\s*(Verse|Chorus|Pre-?Chorus|Bridge|Hook|Outro|Intro|Refrain|Interlude|Final Chorus|Post-?Chorus)(\s*\d*)?\s*(\(.*?\))?\s*$/gim, '');

  // Remove "Cover Art URL: ..." lines
  lyrics = lyrics.replace(/^Cover Art URL:.*$/gm, '');

  // Clean up extra blank lines
  lyrics = lyrics.replace(/\n{3,}/g, '\n\n').trim();

  return lyrics || null;
}

/**
 * Convert error to user-friendly message
 */
function getErrorMessage(error) {
  const errorName = error?.name || '';
  const errorMessage = error?.message || '';

  if (errorName === 'NotReadableError' || errorMessage.includes('permission')) {
    return 'File cannot be read. It may have been moved or deleted.';
  }

  if (errorName === 'EncodingError' || errorMessage.includes('decode') || errorMessage.includes('Unable to decode')) {
    return 'Unable to decode audio. The file may be corrupted or in an unsupported format.';
  }

  if (errorName === 'NotSupportedError') {
    return 'Audio format not supported by your browser.';
  }

  if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
    return 'Network error while reading file.';
  }

  return 'Failed to load audio file.';
}

/**
 * Initialize durations for all playlist tracks asynchronously
 * This runs in the background without blocking the UI
 */
async function initializePlaylistDurations() {
  // Cancel any previous initialization in progress
  if (durationInitController) {
    durationInitController.abort();
  }

  // Mark durations as not loaded when starting
  playlistState.durationsLoaded = false;

  // Create new abort controller for this initialization run
  durationInitController = new AbortController();
  const signal = durationInitController.signal;

  // Create a dedicated audio context for decoding if needed
  // Using a separate context avoids interfering with playback
  let decodeContext = audioState.audioContext;
  if (!decodeContext) {
    decodeContext = new AudioContext();
  }

  // Process tracks one at a time to avoid memory pressure
  for (let i = 0; i < playlistState.playlist.length; i++) {
    // Check if aborted (new files added or playlist cleared)
    if (signal.aborted) {
      console.log('[FileHandler] Duration initialization aborted');
      return;
    }

    const track = playlistState.playlist[i];

    // Skip if duration already set or already has error
    if (track.duration !== null || track.error) {
      continue;
    }

    try {
      // Yield to main thread before processing to keep UI responsive
      await new Promise(resolve => setTimeout(resolve, 0));

      // Check if aborted after yield
      if (signal.aborted) return;

      // Decode audio to get duration
      const arrayBuffer = await track.file.arrayBuffer();

      // Check if aborted after file read
      if (signal.aborted) return;

      const audioBuffer = await decodeContext.decodeAudioData(arrayBuffer);

      // Check if aborted after decode
      if (signal.aborted) return;

      // Set duration and audio buffer on the track
      track.duration = audioBuffer.duration;
      track.audioBuffer = audioBuffer;

      // Update UI to show the new duration (targeted update preserves scroll state)
      updateTrackDuration(i, track.duration);

      console.log(`[FileHandler] Initialized duration for track ${i + 1}/${playlistState.playlist.length}: ${track.name}`);
    } catch (error) {
      // Don't fail the whole process if one track fails
      console.error(`[FileHandler] Failed to get duration for ${track.name}:`, error);

      // Store error on the track for UI display
      track.error = true;
      track.errorMessage = getErrorMessage(error);

      // Update UI to show the error
      updatePlaylistUI();
    }
  }

  // Mark all durations as loaded (regardless of success/failure)
  playlistState.durationsLoaded = true;

  // Dispatch event to notify UI components
  document.dispatchEvent(new CustomEvent('playlistDurationsLoaded'));

  console.log('[FileHandler] Duration initialization complete');
}

/**
 * Cancel any in-progress duration initialization
 * Called when playlist is cleared to stop background processing
 */
export function cancelDurationInitialization() {
  if (durationInitController) {
    durationInitController.abort();
    durationInitController = null;
  }
  // Reset the durations loaded state
  playlistState.durationsLoaded = false;
}

/**
 * Resume duration initialization if not complete
 * Called after export to continue loading durations in the background
 */
export function resumeDurationInitialization() {
  // Only resume if durations aren't fully loaded yet
  if (!playlistState.durationsLoaded && playlistState.playlist.length > 0) {
    initializePlaylistDurations();
  }
}

/**
 * Mark durations as loaded if all tracks already have durations (or errors).
 * Called after adding pre-resolved tracks (e.g., from Suno library or generator)
 * where durations come from API metadata rather than local file decoding.
 * Safe to call alongside initializePlaylistDurations — does nothing if
 * some tracks still need decoding.
 */
export function markDurationsLoadedIfReady() {
  if (playlistState.durationsLoaded) return; // Already done
  if (playlistState.playlist.length === 0) return;

  const allResolved = playlistState.playlist.every(t => t.duration !== null || t.error);
  if (allResolved) {
    playlistState.durationsLoaded = true;
    document.dispatchEvent(new CustomEvent('playlistDurationsLoaded'));
    console.log('[FileHandler] All durations already resolved, marking as loaded');
  }
}

/**
 * Handle multiple files added (from drop or file input)
 */
export async function handleFilesAdded(files) {
  // Block file additions during export to prevent playlist modifications
  if (exportState.isExporting) {
    console.log('[FileHandler] File addition blocked: export in progress');
    return;
  }

  // Prevent concurrent file processing (e.g., rapid double-drops)
  if (isProcessingFiles) {
    console.log('[FileHandler] File addition blocked: already processing');
    return;
  }
  isProcessingFiles = true;

  try {
    const audioFiles = [];
    const txtFiles = new Map(); // Maps audio filename -> txt File

    // Separate audio and txt files
    for (const file of files) {
      if (file.type.startsWith('audio/')) {
        audioFiles.push(file);
      } else if (file.name.endsWith('.txt')) {
        // Txt files are named like "audiofile.wav.txt"
        // Strip .txt to get the audio filename it corresponds to
        const audioFileName = file.name.replace(/\.txt$/, '');
        txtFiles.set(audioFileName, file);
      }
    }

    // Sort audio files alphanumerically for consistent, deterministic import order
    // Using localeCompare with numeric option for natural sorting (e.g., "track2" before "track10")
    audioFiles.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));

    // Check playlist limits
    const maxSongs = await entitlementService.getMaxPlaylistSongs();
    const currentCount = playlistState.playlist.length;
    const availableSlots = maxSongs - currentCount;

    if (availableSlots <= 0) {
      // No slots available - use feature gate to show appropriate paywall
      await featureGate.gatePlaylistAddition(currentCount, audioFiles.length);
      return;
    }

    // Limit audio files to available slots
    if (audioFiles.length > availableSlots) {
      // Show paywall message but allow partial addition
      const isPaid = await featureGate.isPaidTier();
      const isAuthenticated = featureGate.isAuthenticated();
      featureGate.showPaywall({
        feature: 'playlist',
        title: 'Playlist Limit',
        message: `Only ${availableSlots} of ${audioFiles.length} songs can be added. Your plan allows up to ${maxSongs} songs.`,
        ctaText: !isAuthenticated ? 'Sign Up Free' : (isPaid ? 'Upgrade Plan' : 'View Plans')
      });
      // Truncate to available slots
      audioFiles.length = availableSlots;
    }

    // Process audio files and auto-associate lyrics
    for (const audioFile of audioFiles) {
      const item = {
        file: audioFile,
        name: audioFile.name,
        included: true,
        duration: null,
        audioBuffer: null,
        lyrics: null,
        lyricsFile: null,
        error: false,
        errorMessage: null,
        addedAt: Date.now() // Timestamp for date-based sorting
      };

      // Check for matching lyrics file
      // Txt file should be named "audiofile.wav.txt" -> after stripping .txt = "audiofile.wav"
      const matchingTxt = txtFiles.get(audioFile.name);
      if (matchingTxt) {
        item.lyrics = await extractLyrics(matchingTxt);
        item.lyricsFile = matchingTxt;
      }

      playlistState.playlist.push(item);
    }

    if (playlistState.playlist.length > 0) {
      // Track files loaded event
      const totalDuration = playlistState.playlist.reduce((sum, t) => sum + (t.duration || 0), 0);
      trackFilesLoaded(audioFiles.length, totalDuration);

      updatePlaylistUI();

      // Initialize custom order for playlist sorting
      initializeCustomOrder();

      // Stop live mode if active - playlist takes priority
      if (uiState.isLiveMode) {
        exitLiveMode();
      }

      // Load and autoplay first track if not already loaded
      if (!audioState.audioBuffer && playlistState.playlist.length > 0) {
        const loaded = await loadTrack(0);
        if (loaded) {
          startPlayback(0);
        }
      }

      // Start async duration initialization for all tracks
      // This runs in the background and updates the UI as durations are determined
      initializePlaylistDurations();
    }
  } finally {
    isProcessingFiles = false;
  }
}

/**
 * Set up file input and drop zone event listeners
 */
export function setupFileHandlers() {
  // File select button
  if (elements.selectFilesBtn) {
    elements.selectFilesBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      elements.fileInput.click();
    });
  }

  // Folder select button
  if (elements.selectFolderBtn) {
    elements.selectFolderBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      elements.folderInput.click();
    });
  }

  // Drop zone drag events
  if (elements.dropZone) {
    elements.dropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      elements.dropZone.classList.add('drag-over');
    });

    elements.dropZone.addEventListener('dragleave', () => {
      elements.dropZone.classList.remove('drag-over');
    });

    elements.dropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      elements.dropZone.classList.remove('drag-over');
      handleFilesAdded(e.dataTransfer.files);
    });
  }

  // File input change
  if (elements.fileInput) {
    elements.fileInput.addEventListener('change', (e) => {
      handleFilesAdded(e.target.files);
      elements.fileInput.value = ''; // Reset for future selections
    });
  }

  // Folder input change
  if (elements.folderInput) {
    elements.folderInput.addEventListener('change', (e) => {
      handleFilesAdded(e.target.files);
      elements.folderInput.value = ''; // Reset for future selections
    });
  }
}
